import SendIcon from "@mui/icons-material/Send";
import {
    Box,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    OutlinedInput,
    Radio,
    RadioGroup,
    Select,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState, ReactNode } from "react";

import {
    Availability,
    AvailabilityPricingCategory,
    OnBookingCompletedCallback,
    useCreateBooking,
} from "../../apiHooks";
import { ExpandableContainer, Typography as UICoreTypography } from "../../components";
import { ErrorBoundary, WarningMessage } from "../../components";
import { Price } from "../../components/Price";
import { BookingQuestionFragment } from "../../gql-request";
import { useNextTranslation } from "../../hooks";
import { usePriceFormatter } from "../../hooks/usePriceFormatter";
import { ProductAvailabilityPricingCategory } from "../../product/ProductAvailabilityPricingCategory";
import { ProductBookNowButton, ProductBookNowButtonProps } from "../../product/ProductBookNowButton";
import type { Price as PriceType } from "../../types/Price";
import { BookingConsumerTripInput } from "../../types/ProductFullLoaded";
import {
    AvailabilityFormProvider,
    useAvailabilityFormInputOption,
    useAvailabilityFormSelector,
    useAvailabilityFormSend,
} from "./AvailabilityFormProvider";

const AvailabilityButton = styled(ProductBookNowButton)({
    height: 56,
});

export type OnBookingSubmitProps = Parameters<ReturnType<typeof useCreateBooking>[0]>[0];

type AvailabilityFormProps = {
    availabilityId: string;
    initialAvailability?: Availability;
    onBookingCompleted?: OnBookingCompletedCallback;
    bookingReference?: string;
    BookingButtonComponent?: (props: ProductBookNowButtonProps) => JSX.Element;
    shouldAutoFillQuestions: boolean;
    consumerTrip?: BookingConsumerTripInput;
    submitButtonLabel?: string;
    onBookingSubmit?: (props: OnBookingSubmitProps) => Promise<void>;
    saleCurrency?: string;
};
export const AvailabilityForm = ({
    availabilityId,
    initialAvailability,
    onBookingCompleted,
    bookingReference,
    BookingButtonComponent,
    shouldAutoFillQuestions,
    consumerTrip,
    submitButtonLabel,
    onBookingSubmit,
    saleCurrency,
}: AvailabilityFormProps) => {
    const [t] = useNextTranslation("availability");

    return (
        <AvailabilityFormProvider
            key={availabilityId}
            availabilityId={availabilityId}
            saleCurrency={saleCurrency}
            initialAvailability={initialAvailability}
        >
            <ErrorBoundary
                fallback={() => {
                    return <WarningMessage align="left" warning={t("message.errorCannotLoadAvailability")} />;
                }}
            >
                <AvailabilityFormImplementation
                    onBookingCompleted={onBookingCompleted}
                    bookingReference={bookingReference}
                    consumerTrip={consumerTrip}
                    BookingButtonComponent={BookingButtonComponent}
                    shouldAutoFillQuestions={shouldAutoFillQuestions}
                    submitButtonLabel={submitButtonLabel}
                    onBookingSubmit={onBookingSubmit}
                />
            </ErrorBoundary>
        </AvailabilityFormProvider>
    );
};
type AvailabilityFormImplementationProps = {
    onBookingCompleted?: OnBookingCompletedCallback;
    bookingReference?: string;
    BookingButtonComponent?: (props: ProductBookNowButtonProps) => JSX.Element;
    shouldAutoFillQuestions: boolean;
    consumerTrip?: BookingConsumerTripInput;
    submitButtonLabel?: string;
    onBookingSubmit?: (props: OnBookingSubmitProps) => Promise<void>;
};
const AvailabilityFormImplementation = ({
    onBookingCompleted,
    bookingReference,
    BookingButtonComponent,
    shouldAutoFillQuestions,
    consumerTrip,
    submitButtonLabel,
    onBookingSubmit,
}: AvailabilityFormImplementationProps) => {
    const error = useAvailabilityFormSelector((state) => state.matches("error") && state.context.availabilityError);
    if (error) {
        throw error;
    }

    return (
        <AvailabilityFormImplementationWrapper>
            <AvailabilityOptionsPricingWrapper>
                <AvailabilityOptions />

                <PricingCategories />
            </AvailabilityOptionsPricingWrapper>

            <AvailabilityStatusBookingWrapper>
                <AvailabilityStatusInfo />

                <BookingButton
                    onBookingCompleted={onBookingCompleted}
                    bookingReference={bookingReference}
                    consumerTrip={consumerTrip}
                    BookingButtonComponent={BookingButtonComponent}
                    shouldAutoFillQuestions={shouldAutoFillQuestions}
                    submitButtonLabel={submitButtonLabel}
                    onBookingSubmit={onBookingSubmit}
                />
            </AvailabilityStatusBookingWrapper>
        </AvailabilityFormImplementationWrapper>
    );
};

const AvailabilityFormImplementationWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
}));

const AvailabilityOptionsPricingWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
}));

const AvailabilityStatusBookingWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
}));

const InfoWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minHeight: theme.spacing(2.5),
    "& > span": {
        width: "100%",
    },
}));

function AvailabilityOptions() {
    const availability = useAvailabilityFormSelector((state) => state.context.availability);
    const isLoading = useAvailabilityFormSelector((state) => state.hasTag("loading"));
    const isBooking = useAvailabilityFormSelector((state) => state.matches("booking"));

    const optionList = availability?.optionList.nodes ?? [];

    if (!optionList.length) {
        return null;
    }

    const isDisabled = isLoading || isBooking;

    return (
        <Options>
            {optionList.map((option) => {
                switch (option.dataType) {
                    case "BOOLEAN":
                    case "YES/NO":
                        return <BooleanOption key={option.id} option={option} disabled={isDisabled} />;
                    case "OPTIONS":
                        return <SelectOption key={option.id} option={option} disabled={isDisabled} />;
                    case "TEXT":
                        return <TextOption key={option.id} option={option} disabled={isDisabled} />;
                    default:
                        throw new Error(
                            `Unsupported option data type: ${option.dataType}. Full option: ${JSON.stringify(option)}`
                        );
                }
            })}
        </Options>
    );
}

function PricingCategories() {
    const availability = useAvailabilityFormSelector((state) => state.context.availability);
    const showPricingCategories = useAvailabilityFormSelector((state) => state.hasTag("pricingCategoriesVisible"));
    const isLoading = useAvailabilityFormSelector((state) => state.hasTag("loading"));
    const isBooking = useAvailabilityFormSelector((state) => state.matches("booking"));
    const totalDiscount = useAvailabilityFormSelector(
        (state) =>
            state.context.availability?.pricingCategoryList?.nodes.reduce(
                (acc, pricingCategory) => acc + pricingCategory.totalDiscountApplied.gross,
                0
            ) ?? 0
    );

    if (!showPricingCategories || !availability) {
        return null;
    }

    const pricingCategoryList = availability.pricingCategoryList?.nodes ?? [];
    const isDisabled = isLoading || isBooking;

    return (
        <PricingCategoriesWrapper>
            <AvailabilityFormUnitPrice />

            {pricingCategoryList.map((pricingCategory) => (
                <PricingCategory
                    showPrice={!availability.unitPrice.gross || pricingCategory.unitPrice.gross !== 0}
                    key={pricingCategory.id}
                    pricingCategory={pricingCategory}
                    disabled={isDisabled}
                />
            ))}

            <TotalPrice
                totalDiscount={totalDiscount}
                price={{
                    amount: availability.totalPrice.gross,
                    currency: availability.totalPrice.currency,
                }}
            />
        </PricingCategoriesWrapper>
    );
}

function AvailabilityStatusInfo() {
    const isLoading = useAvailabilityFormSelector((state) => state.hasTag("loading"));

    const [t] = useNextTranslation("availabilityForm");
    const bookingError = useAvailabilityFormSelector((state) => state.context.bookingError);
    const optionCombinationSoldOut = useAvailabilityFormSelector((state) => state.context.availability?.soldOut);

    return (
        <InfoWrapper>
            {isLoading && <LinearProgress />}

            {!isLoading && (
                <>
                    <MinMaxMessages />
                    {!bookingError && optionCombinationSoldOut && (
                        <WarningMessageStyled align="left" warning={t("availability:message.optionSoldOut")} />
                    )}
                    {bookingError && <WarningMessageStyled align="left" warning={t("availability:message.soldOut")} />}
                </>
            )}
        </InfoWrapper>
    );
}

function MinMaxMessages() {
    const availability = useAvailabilityFormSelector((state) => state.context.availability);

    const [t] = useNextTranslation("availabilityForm");

    if (!availability) {
        return null;
    }

    let text: string | undefined;
    const min = availability.minParticipants;
    const max = availability.maxParticipants;
    const hasMin = min !== null && min > 1;
    const totalCount =
        availability.pricingCategoryList?.nodes.reduce((acc, pricingCategory) => acc + pricingCategory.units, 0) ?? 0;

    if (hasMin && totalCount < min) {
        text = t("message.minimumConstraint", { min });
    } else if (max && totalCount > max) {
        text = t("message.maximumConstraint", { max });
    }

    if (text) {
        return <WarningMessageStyled align="left" warning={text} />;
    }

    return null;
}

const WarningMessageStyled = styled(WarningMessage)({
    marginTop: 0,
    paddingBlock: 0,
});

type BookingButtonProps = {
    onBookingCompleted?: OnBookingCompletedCallback;
    bookingReference?: string;
    BookingButtonComponent?: (props: ProductBookNowButtonProps) => JSX.Element;
    shouldAutoFillQuestions: boolean;
    consumerTrip?: BookingConsumerTripInput;
    onBookingSubmit?: (props: OnBookingSubmitProps) => Promise<void>;
    submitButtonLabel?: string;
};
const BookingButton = ({
    onBookingCompleted,
    bookingReference,
    BookingButtonComponent,
    shouldAutoFillQuestions,
    consumerTrip,
    submitButtonLabel,
    onBookingSubmit,
}: BookingButtonProps) => {
    const [t] = useNextTranslation("availabilityForm");
    const availability = useAvailabilityFormSelector((state) => state.context.availability);
    const isBookingLoading = useAvailabilityFormSelector((state) => state.matches("booking"));
    const isBookNowAvailable = !!availability?.isValid;
    const bookingHasError = useAvailabilityFormSelector((state) => !!state.context.bookingError);
    const isLoading = useAvailabilityFormSelector((state) => state.hasTag("loading"));
    const send = useAvailabilityFormSend();
    const BookingButton = BookingButtonComponent ?? AvailabilityButton;

    const [_baseCreateBooking] = useCreateBooking({ onBookingCompleted });
    const createBooking = (...params: Parameters<typeof _baseCreateBooking>) => {
        send({ type: "START_BOOKING" });
        void _baseCreateBooking(...params).catch((error) => {
            send({ type: "ERROR_BOOKING", error });
        });
    };

    const handleBookingOnClick = () => {
        if (availability) {
            const params = {
                availabilityId: availability.id,
                reference: bookingReference,
                autoFillQuestions: shouldAutoFillQuestions,
                consumerTrip,
            };
            if (onBookingSubmit) {
                send({ type: "START_BOOKING" });
                void onBookingSubmit(params).catch((error) => {
                    send({ type: "ERROR_BOOKING", error });
                });
            } else {
                void createBooking(params);
            }
        }
    };

    return (
        <FormControlStyled fullWidth margin="normal">
            <BookingButton
                disabled={isLoading || isBookingLoading || !isBookNowAvailable || bookingHasError}
                onClick={handleBookingOnClick}
                isLoading={isBookingLoading}
            >
                {submitButtonLabel ?? t("label.bookNow")}
            </BookingButton>
        </FormControlStyled>
    );
};

const FormControlStyled = styled(FormControl)({
    marginTop: 0,
});

type TotalPriceProps = {
    price: PriceType;
    totalDiscount: number;
};

function TotalPrice({ price, totalDiscount }: TotalPriceProps) {
    const priceFormatter = usePriceFormatter(price.currency);
    const [t] = useNextTranslation("availabilityForm");
    return (
        <Box textAlign="right">
            <Typography component="div" variant="caption">
                {t("label.total")}
            </Typography>
            {totalDiscount > 0 && (
                <>
                    <Typography component="span" sx={{ color: "text.secondary", textDecoration: "line-through" }}>
                        ({priceFormatter(price.amount + totalDiscount)})
                    </Typography>
                    &nbsp; &nbsp;
                </>
            )}
            <Typography component="span" sx={{ color: "text.primary", fontWeight: "bold" }} data-testid="total-price">
                {priceFormatter(price.amount)}
            </Typography>
            {totalDiscount > 0 && (
                <>
                    <br />
                    <Typography component="span" sx={{ color: "text.secondary" }}>
                        {t("message.youSave")}: {priceFormatter(totalDiscount)}
                    </Typography>
                </>
            )}
        </Box>
    );
}

const Options = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
}));

const PricingCategoriesWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    borderTop: `1px dashed ${theme.palette.divider}`,
    paddingTop: theme.spacing(2),
}));
type OptionProps = {
    option: BookingQuestionFragment;
    disabled: boolean;
};

const BooleanOption = ({ option, disabled }: OptionProps) => {
    const send = useAvailabilityFormSend();
    const inputOption = useAvailabilityFormInputOption(option);
    const [t] = useNextTranslation("general");

    const label = option.placeholder ?? option.label;

    const value = inputOption?.value ? (inputOption.value === "1" ? "yes" : "no") : undefined;

    return (
        <FormControl component="fieldset">
            <FormLabel color="primary" component="legend" style={{ color: "gray" }}>
                {label}
            </FormLabel>
            <RadioGroup
                row
                aria-label={label ?? ""}
                value={value}
                onChange={(e) => {
                    send({ type: "SET_OPTION", id: option.id, value: e.target.value === "yes" ? "1" : "0" });
                }}
            >
                <FormControlLabel disabled={disabled} value="yes" control={<Radio />} label={t("yes")} />
                <FormControlLabel disabled={disabled} value="no" control={<Radio />} label={t("no")} />
            </RadioGroup>
        </FormControl>
    );
};

const OutlinedInputStyled = styled(OutlinedInput)((props) => ({
    backgroundColor: props.theme.palette.background.paper,
}));
const TextOption = ({ option, disabled }: OptionProps) => {
    const send = useAvailabilityFormSend();
    const inputOption = useAvailabilityFormInputOption(option);
    const [value, setValue] = useState(inputOption?.value ?? "");

    const fieldId = `option-${option.id}`;
    const label = option.placeholder ?? option.label;
    return (
        <FormControl fullWidth key={option.id} variant="outlined" disabled={disabled}>
            <OutlinedInputStyled
                id={fieldId}
                label={label}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton
                            color="primary"
                            disabled={value === (inputOption?.value ?? "")}
                            onClick={() => {
                                send({ type: "SET_OPTION", id: option.id, value });
                            }}
                        >
                            <SendIcon />
                        </IconButton>
                    </InputAdornment>
                }
            />
        </FormControl>
    );
};

const SelectStyled = styled(Select)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
}));

type ValidBookingQuestionAvailableOptionType = { label: string; value: string; __typename: "AvailableOptions" };
const SelectOption = ({ option, disabled }: OptionProps) => {
    const send = useAvailabilityFormSend();
    const inputOption = useAvailabilityFormInputOption(option);

    const fieldId = `option-${option.id}`;
    const label = option.placeholder ?? option.label;
    return (
        <FormControl fullWidth key={option.id} variant="outlined" disabled={disabled}>
            <InputLabel htmlFor={fieldId}>{label}</InputLabel>
            <SelectStyled
                id={fieldId}
                value={inputOption?.value ?? ""}
                onChange={(e) => {
                    send({ type: "SET_OPTION", id: option.id, value: e.target.value as string });
                }}
                label={label}
            >
                {option.availableOptions
                    .filter(
                        (availableOption): availableOption is ValidBookingQuestionAvailableOptionType =>
                            !!availableOption && !!availableOption.value && !!availableOption.label
                    )
                    .map((availableOption) => {
                        return (
                            <MenuItem key={availableOption.value} value={availableOption.value}>
                                {availableOption.label}
                            </MenuItem>
                        );
                    })}
            </SelectStyled>
        </FormControl>
    );
};

type PricingCategoryProps = {
    pricingCategory: AvailabilityPricingCategory;
    disabled: boolean;
    showPrice: boolean;
};
const PricingCategory = ({ pricingCategory, disabled, showPrice }: PricingCategoryProps) => {
    const inputPricingCategory = useAvailabilityFormSelector((state) =>
        state.context.availabilityInput.pricingCategoryList.find((pc) => pc.id === pricingCategory.id)
    );

    const send = useAvailabilityFormSend();

    let label: ReactNode | string = pricingCategory.label;

    if (pricingCategory.roomPackage) {
        const roomList: ReactNode[] = [];
        for (const room of pricingCategory.roomPackage.roomList.nodes) {
            //Augment label with room details for demo purposes
            const { name, bedGroupList, viewList, occupancy, rateAmenityList, quantity, roomAmenityList } = room;
            // const breakfastAmenity = rateAmenityList.nodes.find(({ id }) => id === "2205");
            const label = `${quantity} x ${name}`;
            const occupancySplit = occupancy.split("-");
            const adultCount = occupancySplit[0] ? Number(occupancySplit[0]) : 0;
            const childCount = occupancySplit[1] ? occupancySplit[1].split(",").length : 0;

            const mainContentList = [];

            const rateAmenityContent = rateAmenityList.nodes.map(({ name }) => name).join(", ");
            if (rateAmenityContent) {
                mainContentList.push(rateAmenityContent);
            }

            const bedGroupListContent = bedGroupList.nodes.map(({ name }) => name).join(" or ");
            if (bedGroupListContent) {
                mainContentList.push(bedGroupListContent);
            }

            const viewListListContent = viewList.nodes.map(({ name }) => name).join(" or ");
            if (viewListListContent) {
                mainContentList.push(viewListListContent);
            }

            mainContentList.push(`${adultCount} ${adultCount > 1 ? "Adults" : "Adult"}`);

            if (childCount > 0) {
                mainContentList.push(`${childCount} ${childCount > 1 ? "Children" : "Child"}`);
            }

            const mainContent = mainContentList.join(", ");

            roomList.push(
                <>
                    <Typography title={label}>{label}</Typography>
                    <UICoreTypography size="small">{mainContent}</UICoreTypography>
                    {!!roomAmenityList.nodes.length && (
                        <ExpandableContainer
                            threshold={0}
                            fadeThreshold={0}
                            expandButtonProps={{
                                size: "small",
                                sx: {
                                    px: 0,
                                    py: 0,
                                },
                            }}
                        >
                            <Typography variant="caption">
                                {roomAmenityList.nodes.map(({ name }) => name).join(", ")}
                            </Typography>
                        </ExpandableContainer>
                    )}
                </>
            );
        }

        label = <LabelWrapper>{roomList.map((room) => room)}</LabelWrapper>;
    }

    return (
        <ProductAvailabilityPricingCategory
            key={pricingCategory.id}
            showPrice={showPrice}
            max={pricingCategory.maxParticipants}
            min={pricingCategory.minParticipants}
            maxParticipantsDepends={pricingCategory.maxParticipantsDepends}
            minAge={pricingCategory.minAge}
            maxAge={pricingCategory.maxAge}
            pricingData={pricingCategory.unitPrice.pricingData}
            label={label}
            description={pricingCategory.description}
            units={inputPricingCategory?.units ?? 0}
            price={{ currency: pricingCategory.unitPrice.currency, amount: pricingCategory.unitPrice.gross }}
            discountsAvailable={pricingCategory.discountsAvailable}
            disabled={disabled || pricingCategory.disabled}
            onUnitsChange={(units: number) => {
                send({ type: "SET_PRICING_CATEGORY_UNITS", id: pricingCategory.id, units });
            }}
        />
    );
};

function AvailabilityFormUnitPrice() {
    const availability = useAvailabilityFormSelector((state) => state.context.availability);
    const { unitPrice, maxParticipants, minParticipants } = availability ?? {};
    const [t] = useNextTranslation("availabilityForm");
    const priceForGroupLabel = t("label.priceForGroup");
    const ofLabel = t("label.of");
    const oneGroupOnlyMessage = t("message.oneGroupOnly");

    if (!unitPrice?.gross) return null;

    let priceString = priceForGroupLabel;
    if (maxParticipants) priceString += ` ${ofLabel} ${minParticipants ?? 0} - ${maxParticipants}`;

    return (
        <div>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography component="span">{priceString}</Typography>
                <Price amount={unitPrice.gross} currency={unitPrice.currency} />
            </Box>
            <Typography variant="caption">{oneGroupOnlyMessage}</Typography>
            <br />
        </div>
    );
}

const LabelWrapper = styled("div")({
    "&": {
        flexGrow: 100,
        gridArea: "label",
    },
});
